import { useMemo, useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { Modal, Text } from "@mantine/core"
import { useModals } from "@mantine/modals"
import { useNotifications } from "@mantine/notifications"
import { Icon } from "@material-tailwind/react"
import { SelectColumnFilter } from "components/ui/table/TableHeader"
import { ToggleActiveCell, EditActions } from "components/ui/table/TableUtils"
import { getByID, patchSupplier, toggleAccess } from "services/api.service"
import useFetchAndLoad from "hooks/useFetchAndLoad.ts"
import Table from "components/ui/table/Table"
import SupplierVehicleForm from "components/ui/forms/SupplierVehicleForm"

const SupplierVehicles = () => {
  const emptySupplierVehicle = {
    brand: "",
    model: "",
    color: "",
    license_plate: "",
  }

  const { supplierId } = useParams()

  const API_ENDPOINT = `/suppliers`
  const FORMATED_SUPPLIER_ID = supplierId.split(":")[1]

  const [supplierVehicles, setSupplierVehicles] = useState([])
  const [supplier, setSupplier] = useState("")
  const [isCreation, setIsCreation] = useState(false)
  const [refresh, setRefresh] = useState(false)
  const [editSupplierPersonData, setEditSupplierVehicleData] = useState(emptySupplierVehicle)
  const [opened, setOpened] = useState(false)

  const modals = useModals()
  const notifications = useNotifications()

  const { loading, callEndpoint } = useFetchAndLoad()

  const setData = async () => {
    await callEndpoint(getByID(API_ENDPOINT, FORMATED_SUPPLIER_ID))
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          setSupplier(response.data.data)
          setSupplierVehicles(response.data.data.vehicles)
        }
      })
      .catch((error) => {
        notifications.showNotification({
          color: "red",
          title: "Error",
          message: "Error a los vehiculos del proveedor",
        })
      })
  }

  useEffect(() => {
    setData()
    localStorage.setItem("path", `${API_ENDPOINT}/${supplierId}/vehicles`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh])

  const onEdit = (vehicle) => {
    setEditSupplierVehicleData(vehicle.original)
    setIsCreation(false)
    setOpened(true)
  }

  const onDelete = (vehicle) => {
    openConfirmDeleteModal(vehicle)
  }

  const openConfirmDeleteModal = (vehicle) => {
    modals.openConfirmModal({
      title: "Por favor confirma la eliminación",
      children: (
        <Text size='sm'>
          ¿Seguro de que deseas eliminar el vehiculo
          <span className='font-bold'>
            {" " + vehicle.original.brand + " con placas " + vehicle.original.license_plate}
          </span>
          ? <br />
          Esta acción no se puede deshacer.
        </Text>
      ),
      labels: { confirm: "Eliminar", cancel: "Cancelar" },
      confirmProps: {
        className: "bg-red-600 text-white hover:bg-red-500",
      },
      onCancel: () => {},
      onConfirm: async () => {
        notifications.showNotification({
          color: "blue",
          title: "Eliminando",
          loading: true,
          message: "Eliminando vehiculo de proveedor.",
        })
        await callEndpoint(
          patchSupplier(`${API_ENDPOINT}/${FORMATED_SUPPLIER_ID}`, "vehicles", vehicle.original._id)
        )
          .then((response) => {
            if (response.status === 200 || response.status === 201) {
              setData()
              notifications.clean()
              notifications.showNotification({
                color: "green",
                title: "Vehiculo de proveedor eliminado",
                message: "El vehiculo del proveedor ha sido eliminado con éxito.",
                icon: <Icon name={"check"} />,
              })
            }
          })
          .catch((error) => {
            notifications.clean()
            notifications.showNotification({
              color: "red",
              title: "Error",
              message: "Error al eliminar al el vehiculo del proveedor.",
              icon: <Icon name={"close"} />,
            })
          })
      },
    })
  }

  const toggleActive = async (person) => {
    await callEndpoint(
      toggleAccess(`${API_ENDPOINT}/${FORMATED_SUPPLIER_ID}`, "vehicles", person.original._id)
    )
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          setData()
          notifications.clean()
          notifications.showNotification({
            color: "green",
            title: `Acceso habilitado`,
            message: `Acceso habilitado para el vehiculo ${person.original.brand} con placas ${person.original.license_plate}.`,
            icon: <Icon name={"check"} />,
          })
        }
      })
      .catch((error) => {
        notifications.clean()
        notifications.showNotification({
          color: "red",
          title: "Error",
          message: `Error al intentar habilitar el acceso para ${person.original.brand} ${person.original.license_plate}.`,
          icon: <Icon name={"close"} />,
        })
      })
  }

  const columns = useMemo(
    () => [
      {
        Header: "Marca",
        accessor: "brand",
        Filter: SelectColumnFilter,
        filter: "includes",
      },
      {
        Header: "Modelo",
        accessor: "model",
        Filter: SelectColumnFilter,
        filter: "includes",
      },
      {
        Header: "Color",
        accessor: "color",
        Filter: SelectColumnFilter,
        filter: "includes",
      },
      {
        Header: "Placa",
        accessor: "license_plate",
      },
      {
        Header: "Activo",
        accessor: "active",
        Cell: ({ row }) => <ToggleActiveCell value={row} onChange={toggleActive} />,
      },
      {
        Header: "Acciones",
        Cell: ({ row }) => <EditActions value={row} onEdit={onEdit} onDelete={onDelete} />,
      },
    ],
    [setData]
  )

  return (
    <>
      <Table
        columns={columns}
        data={supplierVehicles}
        iconName={"construction"}
        editable={true}
        setOpened={setOpened}
        setIsCreation={setIsCreation}
        loading={loading}
        header={`Vehiculos del proveedor ${supplier.name}`}
        nested={"/suppliers"}
      />
      <Modal
        opened={opened}
        onClose={() => {
          setEditSupplierVehicleData(emptySupplierVehicle)
          setOpened(false)
        }}
        title={isCreation ? "Crear Vehiculo" : "Editar Vehiculo"}
        size='xl'>
        <SupplierVehicleForm
          data={editSupplierPersonData}
          refresh={() => {
            setRefresh(!refresh)
            setOpened(false)
            setEditSupplierVehicleData(emptySupplierVehicle)
          }}
          isCreation={isCreation}
          notifications={notifications}
          endpoint={`${API_ENDPOINT}`}
          supplier={supplier}
        />
      </Modal>
    </>
  )
}

export default SupplierVehicles
